import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plus, ArrowUpRight, MoreHorizontal, Download, Wand2 } from 'lucide-react';
import { usePlatformStore } from '@/lib/store';
import { StatusBadge, PlanBadge, SearchInput, Modal, EmptyState, Spinner, Dropdown, DropdownItem } from '@/components/shared/UIComponents';
import { formatRelative, slugify } from '@/utils/helpers';
import { fetchTenantsAdminActivity, fetchTenantCostsRollup, type TenantAdminActivitySummary, type TenantCostRow } from '@/lib/api';
import type { TenantStatus, PlanName, TenantPlan, CreateTenantInput } from '@/types';

/** Format integer US cents → display string. Sub-dollar shows cents,
 *  dollar+ rounds to whole dollars to keep the table column readable. */
function formatCostUsd(cents: number): string {
  if (!cents) return '—';
  if (cents < 100) return `$${(cents / 100).toFixed(2)}`;
  if (cents < 1_000_00) return `$${(cents / 100).toFixed(1)}`;
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

export function TenantsPage() {
  const { tenants, plans, loading, loadTenants, loadPlans, createTenant, suspendTenant } = usePlatformStore();
  // Per-tenant admin-signin-activity counts. One round-trip on mount —
  // separate from the main tenants load so a slow auth.users join doesn't
  // delay the table render. Index by tenant_id for O(1) lookup in the row.
  const [adminActivity, setAdminActivity] = useState<Record<string, TenantAdminActivitySummary>>({});
  useEffect(() => {
    fetchTenantsAdminActivity()
      .then(rows => {
        const map: Record<string, TenantAdminActivitySummary> = {};
        for (const r of rows) map[r.tenant_id] = r;
        setAdminActivity(map);
      })
      .catch(err => console.warn('[tenants] failed to load admin activity:', err?.message || err));
  }, []);

  // Per-tenant cost rollup for the last 30 days. Loaded once on mount,
  // same pattern as admin activity (don't block the table render on a
  // potentially slow cross-project edge fn call).
  const [costMap, setCostMap] = useState<Record<string, TenantCostRow>>({});
  const [costLoading, setCostLoading] = useState(true);
  useEffect(() => {
    fetchTenantCostsRollup(30)
      .then(rows => {
        const map: Record<string, TenantCostRow> = {};
        for (const r of rows) map[r.tenant_id] = r;
        setCostMap(map);
      })
      .catch(err => console.warn('[tenants] failed to load cost rollup:', err?.message || err))
      .finally(() => setCostLoading(false));
  }, []);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TenantStatus | 'all'>('all');
  const [planFilter, setPlanFilter] = useState<PlanName | 'all'>('all');
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { loadTenants(); loadPlans(); }, [loadTenants, loadPlans]);

  const filtered = tenants.filter(t => {
    if (search && !t.name.toLowerCase().includes(search.toLowerCase()) && !t.slug.includes(search.toLowerCase())) return false;
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (planFilter !== 'all' && (t.plan as TenantPlan)?.name !== planFilter) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Tenants</h1>
          <p className="text-surface-500 mt-1">{tenants.length} organisations on the platform</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/tenants/new" className="btn-secondary text-sm"><Wand2 size={14} /> Guided Setup</Link>
          <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus size={16} /> Quick Create</button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-64"><SearchInput value={search} onChange={setSearch} placeholder="Search by name or slug..." /></div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="input-field w-auto py-2 text-sm">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={planFilter} onChange={e => setPlanFilter(e.target.value as any)} className="input-field w-auto py-2 text-sm">
          <option value="all">All plans</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="business">Business</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <div className="flex-1" />
        <button className="btn-ghost text-sm"><Download size={14} /> Export</button>
      </div>

      {/* Table */}
      {loading && tenants.length === 0 ? (
        <div className="flex justify-center py-20"><Spinner size="lg" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={<Building2 size={28} />}
          title={search || statusFilter !== 'all' ? 'No matching tenants' : 'No tenants yet'}
          description={search || statusFilter !== 'all' ? 'Try adjusting your filters.' : 'Create your first tenant to get started.'}
          action={!search && statusFilter === 'all' ? <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus size={16} /> Create First Tenant</button> : undefined} />
      ) : (
        <div className="glass-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-200">
                  <th className="table-header-cell">Organisation</th>
                  <th className="table-header-cell">Plan</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell">Users</th>
                  <th className="table-header-cell">Domain</th>
                  <th className="table-header-cell">Storage</th>
                  <th className="table-header-cell" title="LLM + image + video + audio + other third-party spend over the last 30 days">30d cost</th>
                  <th className="table-header-cell">Admin activity</th>
                  <th className="table-header-cell">Created</th>
                  <th className="table-header-cell w-12"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id} className="table-row group">
                    <td className="table-cell">
                      <Link to={`/admin/tenants/${t.id}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shrink-0 shadow-sm ring-1 ring-slate-500/30">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                        </div>
                        <div>
                          <div className="font-medium text-surface-900">{t.name}</div>
                          <div className="text-xs text-surface-500">{t.slug}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="table-cell"><PlanBadge plan={(t.plan as TenantPlan)?.name || 'free'} label={(t.plan as TenantPlan)?.display_name} /></td>
                    <td className="table-cell"><StatusBadge status={t.status} /></td>
                    <td className="table-cell">
                      <span className="text-surface-700">{t.current_user_count}</span>
                      <span className="text-surface-400">/{t.max_users || '∞'}</span>
                    </td>
                    <td className="table-cell">
                      <div className="text-xs font-mono text-surface-500">{t.subdomain}</div>
                      {t.custom_domain && <div className="text-xs font-mono text-brand-600 dark:text-brand-400 mt-0.5">{t.custom_domain}</div>}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-surface-100 rounded-full overflow-hidden">
                          <div className="h-full bg-brand-500 rounded-full"
                            style={{ width: `${Math.min(100, (t.storage_used_mb / (t.storage_limit_mb || 1)) * 100)}%` }} />
                        </div>
                        <span className="text-xs text-surface-500">{Math.round((t.storage_used_mb / (t.storage_limit_mb || 1)) * 100)}%</span>
                      </div>
                    </td>
                    <td className="table-cell">
                      {/* 30-day spend rollup — loaded lazily after the table
                          renders so a slow cross-project edge fn doesn't
                          block the page. Sub-dollar shown to 2dp, $1+ to
                          1dp, $100+ as whole dollars. */}
                      {costLoading ? (
                        <span className="text-xs text-surface-400">…</span>
                      ) : costMap[t.id] ? (
                        <Link
                          to={`/admin/tenants/${t.id}#costs`}
                          className="text-xs font-semibold text-surface-900 hover:text-primary-600 transition-colors"
                          title={`LLM $${(costMap[t.id].llm_cents / 100).toFixed(2)} · Image $${(costMap[t.id].image_cents / 100).toFixed(2)} · Video $${(costMap[t.id].video_cents / 100).toFixed(2)} · Audio $${(costMap[t.id].audio_cents / 100).toFixed(2)} · Other $${(costMap[t.id].other_3p_cents / 100).toFixed(2)}`}
                        >
                          {formatCostUsd(costMap[t.id].total_cents)}
                        </Link>
                      ) : (
                        <span className="text-xs text-surface-400">—</span>
                      )}
                    </td>
                    <td className="table-cell"><AdminActivityCell summary={adminActivity[t.id]} tenantStatus={t.status} /></td>
                    <td className="table-cell text-surface-500 text-xs">{formatRelative(t.created_at)}</td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Link to={`/admin/tenants/${t.id}`} className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-500 hover:text-surface-900 transition-colors">
                          <ArrowUpRight size={14} />
                        </Link>
                        <Dropdown trigger={<button className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-500 hover:text-surface-900 transition-colors"><MoreHorizontal size={14} /></button>}>
                          <DropdownItem onClick={() => {}}>Edit</DropdownItem>
                          <DropdownItem onClick={() => {}}>Impersonate</DropdownItem>
                          <DropdownItem onClick={() => suspendTenant(t.id)} danger>Suspend</DropdownItem>
                        </Dropdown>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CreateTenantModal open={showCreate} onClose={() => setShowCreate(false)} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* CREATE TENANT MODAL                                                        */
/* ═══════════════════════════════════════════════════════════════════════════ */

function CreateTenantModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { plans, createTenant, loading } = usePlatformStore();
  const [form, setForm] = useState({ name: '', slug: '', owner_email: '', plan_id: '', primary_color: '#0d9488' });
  const [autoSlug, setAutoSlug] = useState(true);
  const [error, setError] = useState('');

  const set = (field: string, value: string) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'name' && autoSlug) next.slug = slugify(value);
      return next;
    });
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.name.trim()) return setError('Organisation name is required');
    if (!form.slug.trim()) return setError('URL slug is required');
    if (!form.plan_id) return setError('Please select a plan');
    try {
      await createTenant({
        name: form.name, slug: form.slug, plan_id: form.plan_id,
        owner_email: form.owner_email, branding: { primary_color: form.primary_color },
      } as CreateTenantInput);
      onClose();
      setForm({ name: '', slug: '', owner_email: '', plan_id: '', primary_color: '#0d9488' });
      setAutoSlug(true);
    } catch (err) { setError((err as Error).message); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Create New Tenant" subtitle="Set up a new organisation on the platform" wide>
      <div className="space-y-4">
        {error && <div className="p-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-400">{error}</div>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="input-label">Organisation Name *</label>
            <input className="input-field" placeholder="Wanderlust Travel" value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div>
            <label className="input-label">URL Slug *</label>
            <div className="relative">
              <input className="input-field pr-28" placeholder="wanderlust" value={form.slug}
                onChange={e => { setAutoSlug(false); set('slug', e.target.value); }} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-surface-500">.discoverygem.com</span>
            </div>
          </div>
        </div>

        <div>
          <label className="input-label">Owner Email</label>
          <input className="input-field" type="email" placeholder="admin@wanderlust.com" value={form.owner_email} onChange={e => set('owner_email', e.target.value)} />
        </div>

        <div>
          <label className="input-label">Plan *</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {plans.map(p => (
              <button key={p.id} onClick={() => set('plan_id', p.id)}
                className={`p-3 rounded-xl border text-left transition-all ${
                  form.plan_id === p.id ? 'border-brand-500 bg-brand-500/10' : 'border-surface-300 bg-surface-50 hover:border-surface-400'
                }`}>
                <div className="text-sm font-medium text-surface-900 capitalize">{p.name}</div>
                <div className="text-xs text-surface-500 mt-0.5">
                  {p.price_per_seat_monthly === 0 ? 'Free' : `$${p.price_per_seat_monthly / 100}/seat/mo`}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="input-label">Brand Colour</label>
          <div className="flex items-center gap-3">
            <input type="color" value={form.primary_color} onChange={e => set('primary_color', e.target.value)}
              className="w-10 h-10 rounded-lg border-2 border-white/10 cursor-pointer bg-transparent" />
            <input className="input-field w-32" value={form.primary_color} onChange={e => set('primary_color', e.target.value)} />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSubmit} disabled={loading} className="btn-primary">
            {loading ? <Spinner size="sm" /> : <Plus size={16} />}
            Create Tenant
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin activity cell — coarse signal of whether a tenant's admins have
// actually used their accounts. Computed server-side via
// get_tenants_with_admin_activity RPC.
//
// Display priority (top to bottom):
//   1. Tenants with NO admins at all → red ("No admins")
//   2. Any pending invite → amber ("N pending invite(s)")
//   3. Any "never signed in" admin → amber ("N never signed in")
//   4. Any "active" admin (in last 7d) → green ("Active · X ago")
//      — BUT only if the tenant itself is not cancelled/suspended.
//      A suspended tenant with a recent admin sign-in renders as a
//      grey "Last seen · X ago" so the green Active badge doesn't
//      visually contradict the red Suspended status badge. The
//      sign-in still happened (admins of a cancelled tenant can
//      still log in — suspension is a billing flag, not an auth
//      block) but we don't want operators reading the row as
//      "currently active." See tenant-list audit, 2026-06-09.
//   5. Only stale or inactive → grey ("Stale · X ago")
//
// The hover title carries the full breakdown so the operator can drill
// into details without leaving the list view.
// ─────────────────────────────────────────────────────────────────────────────
function AdminActivityCell({
  summary,
  tenantStatus,
}: {
  summary: TenantAdminActivitySummary | undefined;
  tenantStatus?: string;
}) {
  // Still loading? Show a soft skeleton instead of a misleading "no
  // admins" — the activity RPC fires AFTER the tenants list lands.
  if (!summary) return <span className="text-xs text-surface-400">—</span>;

  const { admins_total, admins_pending, admins_never_signed_in, admins_active, most_recent_admin_signin } = summary;

  // Tenant lifecycle says "off." Includes both legacy/current naming
  // — suspendTenant currently writes 'cancelled' but the type allows
  // 'suspended' too; either should mute the green Active badge.
  const isOffline = tenantStatus === 'cancelled' || tenantStatus === 'suspended';

  if (admins_total === 0) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-700"
        title="This tenant has no admins on tenant_admins. Nobody can manage it.">
        No admins
      </span>
    );
  }

  if (admins_pending > 0) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800"
        title={titleFor(summary)}>
        {admins_pending} pending invite{admins_pending === 1 ? '' : 's'}
      </span>
    );
  }

  if (admins_never_signed_in > 0) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800"
        title={titleFor(summary)}>
        {admins_never_signed_in} never signed in
      </span>
    );
  }

  if (admins_active > 0 && most_recent_admin_signin) {
    // Mute on suspended/cancelled tenants — see header comment. The
    // sign-in is real, but framing it as "Active" alongside a
    // Suspended status badge reads as a contradiction. "Last seen"
    // says the same fact more honestly: someone signed in, but the
    // tenant is not currently operational.
    if (isOffline) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-surface-200 text-surface-600"
          title={`Tenant is ${tenantStatus} — admin sign-in still recorded. ${titleFor(summary)}`}>
          Last seen · {formatRelative(most_recent_admin_signin)}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800"
        title={titleFor(summary)}>
        Active · {formatRelative(most_recent_admin_signin)}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-surface-200 text-surface-600"
      title={titleFor(summary)}>
      Stale{most_recent_admin_signin ? ` · ${formatRelative(most_recent_admin_signin)}` : ''}
    </span>
  );
}

function titleFor(s: TenantAdminActivitySummary): string {
  const parts: string[] = [];
  parts.push(`${s.admins_total} total admin${s.admins_total === 1 ? '' : 's'}`);
  if (s.admins_active)          parts.push(`${s.admins_active} active (last 7d)`);
  if (s.admins_stale)           parts.push(`${s.admins_stale} stale (7–30d)`);
  if (s.admins_inactive)        parts.push(`${s.admins_inactive} inactive (30d+)`);
  if (s.admins_never_signed_in) parts.push(`${s.admins_never_signed_in} never signed in`);
  if (s.admins_pending)         parts.push(`${s.admins_pending} pending invite${s.admins_pending === 1 ? '' : 's'}`);
  return parts.join(' · ');
}
